import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { sortBy } from "lodash";
import {
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Divider
} from "@mui/material";
import DualList from "../../../../components/DualList/DualList";
import CourseExclusionsFilters from "./CourseExclusionsFilters";
import {
  fetchCourseExclusions,
  updateExclusions
} from "../../../../redux/actions/contentCoursesActions";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import CourseExclusionSettingsModal from "./CourseExclusionSettingsModal";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import CircularProgress from "@mui/material/CircularProgress";
import { enqueueSuccessSnackbar } from "../../../../redux/actions/snackbarActions";
import ChangeExclusionsNotice from "../../../../components/WarningModal/ChangeExclusionsNotice";

/**
 * Returns an object containing the properties that will be passed into
 * the DualListItem component
 */
const getListItemDetails = (item, unselectedList) => {
  return {
    id: item.id,
    name: item.name,
    firstName: item.firstName,
    lastName: item.lastName,
    subtitles: [item.positionName, item.locationName],
    highlighted: item.assigned && unselectedList
  };
};

const getEmployee = (newList, id) => {
  const selectedEmployee = newList.find(employee => employee.id === id);
  return selectedEmployee;
};

const CourseExclusions = ({ courseId }) => {
  const { t } = useTranslation();
  const employees = useSelector(state => state.contentCoursesState.exclusions);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [changeExclusionsNoticeOpen, setChangeExclusionsNoticeOpen] = useState(
    false
  );
  const [employeeList, setEmployeeList] = useState([]);
  const [unselectedList, setUnselectedList] = useState([]);
  const [selectedList, setSelectedList] = useState([]);
  const [exludedIds, setExcludedIds] = useState([]);
  const [
    assignmentWillBeDestroyedFor,
    setAssignmentWillBeDestroyedFor
  ] = useState([]);
  const [notExcludedIds, setNotExcludedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [resetForm, setResetForm] = useState(false);
  const [filters, setFilters] = useState({
    displayAssignedOnly: false
  });
  const dispatch = useDispatch();

  /**
   * Create an array of key value pairs with the filters for the course exclusions and uses
   * them to fetch the exclusions
   */
  const fetchExclusions = () => {
    setListLoading(true);
    dispatch(fetchCourseExclusions(courseId)).then(() => setListLoading(false));
  };

  useEffect(() => {
    if (employees !== employeeList) {
      setEmployeeList(employees);
      setSelectedList(employees.filter(employee => employee.excluded));
      setUnselectedList(employees.filter(employee => !employee.excluded));
    }
  }, [employees]);

  /**
   * Applying filters
   */
  useEffect(() => {
    let newSelectedList = employeeList.filter(employee => employee.excluded);
    let newUnselectedList = employeeList.filter(employee => !employee.excluded);
    if (filters.employeeName && filters.employeeName !== "") {
      newSelectedList = newSelectedList.filter(
        employee =>
          employee.name
            .toLowerCase()
            .indexOf(filters.employeeName.toLowerCase()) >= 0
      );
      newUnselectedList = newUnselectedList.filter(
        employee =>
          employee.name
            .toLowerCase()
            .indexOf(filters.employeeName.toLowerCase()) >= 0
      );
    }
    if (filters.position && filters.position !== "") {
      newSelectedList = newSelectedList.filter(
        employee => employee.positionName === filters.position
      );
      newUnselectedList = newUnselectedList.filter(
        employee => employee.positionName === filters.position
      );
    }
    if (filters.location && filters.location !== "") {
      newSelectedList = newSelectedList.filter(
        employee => employee.locationName === filters.location
      );
      newUnselectedList = newUnselectedList.filter(
        employee => employee.locationName === filters.location
      );
    }
    if (filters.state && filters.state !== "") {
      newSelectedList = newSelectedList.filter(
        employee => employee.locationState === filters.state
      );
      newUnselectedList = newUnselectedList.filter(
        employee => employee.locationState === filters.state
      );
    }
    if (filters.group && filters.group !== "") {
      newSelectedList = newSelectedList.filter(employee =>
        employee.groups.some(group => group.name === filters.group)
      );
      newUnselectedList = newUnselectedList.filter(employee =>
        employee.groups.some(group => group.name === filters.group)
      );
    }
    if (filters.hiredSince && filters.hiredSince !== "") {
      newSelectedList = newSelectedList.filter(
        employee => new Date(employee.hireDate) >= new Date(filters.hiredSince)
      );
      newUnselectedList = newUnselectedList.filter(
        employee => new Date(employee.hireDate) >= new Date(filters.hiredSince)
      );
    }
    if (filters.hiredBefore && filters.hiredBefore !== "") {
      newSelectedList = newSelectedList.filter(
        employee => new Date(employee.hireDate) < new Date(filters.hiredBefore)
      );
      newUnselectedList = newUnselectedList.filter(
        employee => new Date(employee.hireDate) < new Date(filters.hiredBefore)
      );
    }
    if (filters.displayAssignedOnly) {
      newSelectedList = newSelectedList.filter(
        employee => employee.assigned === filters.displayAssignedOnly
      );
      newUnselectedList = newUnselectedList.filter(
        employee => employee.assigned === filters.displayAssignedOnly
      );
    }
    setSelectedList(newSelectedList);
    setUnselectedList(newUnselectedList);
    setLoading(false);
  }, [employeeList, filters]);

  useEffect(() => {
    fetchExclusions();
  }, []);

  /**
   * Marks all courses as active except for the ones which are filtered out
   */
  const addAll = () => {
    const newList = employeeList;
    const newExludedIdsList = exludedIds;
    const newUnexludedIdsList = notExcludedIds;
    for (let i in unselectedList) {
      newExludedIdsList.push(unselectedList[i].id);
      const selectedEmployee = getEmployee(newList, unselectedList[i].id);
      for (let j in newUnexludedIdsList) {
        if (newUnexludedIdsList[j] === selectedEmployee.id) {
          newUnexludedIdsList.splice(j, 1);
          j--;
        }
      }
      selectedEmployee.excluded = !selectedEmployee.excluded;
    }
    const newAssignmentWillBeDestroyedFor = newList.filter(
      employee =>
        employee.hasRemovableAssignments &&
        newExludedIdsList.includes(employee.id)
    );
    setEmployeeList([...newList]);
    setNotExcludedIds([...newUnexludedIdsList]);
    setExcludedIds([...newExludedIdsList]);
    setAssignmentWillBeDestroyedFor(newAssignmentWillBeDestroyedFor);
  };

  /**
   * Adds all courses to the unselected list
   */
  const removeAll = () => {
    const newList = employeeList;
    const newExludedIdsList = exludedIds;
    const newUnexludedIdsList = notExcludedIds;
    for (let i in selectedList) {
      newUnexludedIdsList.push(selectedList[i].id);
      const selectedEmployee = getEmployee(newList, selectedList[i].id);
      for (let j in newExludedIdsList) {
        if (newExludedIdsList[j] === selectedEmployee.id) {
          newExludedIdsList.splice(j, 1);
          j--;
        }
      }
      selectedEmployee.excluded = !selectedEmployee.excluded;
    }
    setEmployeeList([...newList]);
    setNotExcludedIds([...newUnexludedIdsList]);
    setExcludedIds([...newExludedIdsList]);
    setAssignmentWillBeDestroyedFor([]);
  };

  const changeExclusionStatus = selectedItem => {
    const newList = employeeList;
    const selectedEmployee = newList.find(
      employee => employee.id === selectedItem.id
    );
    selectedEmployee.excluded = !selectedEmployee.excluded;
    setEmployeeList([...newList]);
    let newExludedIdsList = exludedIds;
    let newUnexludedIdsList = notExcludedIds;
    if (selectedEmployee.excluded) {
      newExludedIdsList.push(selectedEmployee.id);
      for (let i in newUnexludedIdsList) {
        if (newUnexludedIdsList[i] === selectedEmployee.id) {
          newUnexludedIdsList.splice(i, 1);
          i--;
        }
      }
      if (selectedEmployee.hasRemovableAssignments) {
        setAssignmentWillBeDestroyedFor([
          ...assignmentWillBeDestroyedFor,
          selectedEmployee
        ]);
      }
    } else {
      newUnexludedIdsList.push(selectedEmployee.id);
      for (let j in newExludedIdsList) {
        if (newExludedIdsList[j] === selectedEmployee.id) {
          newExludedIdsList.splice(j, 1);
          j--;
        }
      }
      if (assignmentWillBeDestroyedFor.length > 0) {
        const assignmentWillBeDestroyedForCopy = assignmentWillBeDestroyedFor.filter(
          obj => obj.id !== selectedEmployee.id
        );
        setAssignmentWillBeDestroyedFor(assignmentWillBeDestroyedForCopy);
      }
    }
    setNotExcludedIds([...newUnexludedIdsList]);
    setExcludedIds([...newExludedIdsList]);
  };

  const handleSaveExclusions = (values, successCallback, errorCallback) => {
    setSaving(true);
    const valuesCopy = { ...values };
    valuesCopy.idsToAdd = exludedIds;
    valuesCopy.idsToRemove = notExcludedIds;
    dispatch(
      updateExclusions(
        courseId,
        valuesCopy,
        response => {
          setLoading(true);
          setResetForm(true);
          setExcludedIds([]);
          setNotExcludedIds([]);
          setAssignmentWillBeDestroyedFor([]);
          setFilters({ displayAssignedOnly: false });
          setSettingsModalOpen(false);
          if (successCallback && typeof successCallback === "function") {
            successCallback();
            dispatch(
              enqueueSuccessSnackbar({
                message: t("courseExclusions.successExclusionsSaveMsg", {
                  count: (response.data || Object.values(response.user)).filter(
                    user => user.attributes.excluded === true
                  ).length
                })
              })
            );
          }
        },
        errorCallback
      )
    ).then(() => setSaving(false));
  };

  const continueSavingProcess = () => {
    setChangeExclusionsNoticeOpen(false);
    setSettingsModalOpen(true);
  };

  const handleFiltersChange = submittedFilters => {
    setResetForm(false);
    setLoading(true);
    setFilters({ ...filters, ...submittedFilters });
  };

  return (
    <div>
      {!listLoading ? (
        <>
          <CourseExclusionSettingsModal
            onClose={() => setSettingsModalOpen(false)}
            open={settingsModalOpen}
            onSubmit={handleSaveExclusions}
            title={t("courseExclusions.settingsModal.title")}
          />
          <ChangeExclusionsNotice
            onClose={() => setChangeExclusionsNoticeOpen(false)}
            open={changeExclusionsNoticeOpen}
            onSubmit={continueSavingProcess}
            employees={assignmentWillBeDestroyedFor}
          />
          <Box my={3}>
            <CourseExclusionsFilters
              onFilterSubmit={handleFiltersChange}
              loading={loading}
              isResetForm={resetForm}
            />
          </Box>
          <Divider />
          <Box my={3}>
            <DualList
              onAddAll={addAll}
              onRemoveAll={removeAll}
              onAddToSelectedList={changeExclusionStatus}
              onAddToUnselectedList={changeExclusionStatus}
              selectedListHeader={`${t("courseExclusions.excludedEmployees")}`}
              unselectedListHeader={`${t(
                "courseExclusions.employeesWithoutExclusion"
              )}`}
              selectedList={sortBy(
                selectedList.map(item => getListItemDetails(item, false)),
                [
                  user => user.lastName.toLowerCase(),
                  user => user.firstName.toLowerCase()
                ]
              )}
              unselectedList={sortBy(
                unselectedList.map(item => getListItemDetails(item, true)),
                [
                  user => user.lastName.toLowerCase(),
                  user => user.firstName.toLowerCase()
                ]
              )}
              unselectedListAction={
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.displayAssignedOnly}
                      onChange={e =>
                        handleFiltersChange({
                          displayAssignedOnly: !filters.displayAssignedOnly
                        })
                      }
                      value="assignedOnly"
                      color="primary"
                    />
                  }
                  label={t("courseExclusions.displayAssignedEmployeesOnly")}
                />
              }
              renderList={(items, DualListItem) => (
                <Box flex={1} minHeight={460} mt={3}>
                  <AutoSizer>
                    {({ height, width }) => (
                      <List
                        className="List"
                        height={height}
                        itemCount={items.length}
                        itemSize={89}
                        width={width}
                      >
                        {({ index, style }) => {
                          return (
                            <div style={style}>
                              {DualListItem(items[index])}
                            </div>
                          );
                        }}
                      </List>
                    )}
                  </AutoSizer>
                </Box>
              )}
            />
          </Box>
          <Divider />
          <Box display="flex" justifyContent="flex-end" mt={5}>
            <Button
              variant="contained"
              color="primary"
              disabled={!!saving}
              onClick={() => {
                if (assignmentWillBeDestroyedFor.length > 0) {
                  setChangeExclusionsNoticeOpen(true);
                } else if (exludedIds.length > 0) {
                  setSettingsModalOpen(true);
                } else {
                  handleSaveExclusions({});
                }
              }}
            >
              {saving ? (
                <>
                  <CircularProgress size={15} color="inherit" />
                  <span style={{ paddingLeft: "10px" }}>
                    {t("courseExclusions.saveExclusions")}
                  </span>
                </>
              ) : (
                t("courseExclusions.saveExclusions")
              )}
            </Button>
          </Box>
        </>
      ) : (
        <LoadingSpinner />
      )}
    </div>
  );
};

CourseExclusions.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      courseId: PropTypes.string
    })
  })
};

export default CourseExclusions;
